import type { Accessor } from 'solid-js'
import type { UploadedImageData } from 'Shared/posters/types'

export interface UploadContext {
	/**
	 * Upload a file and return the raw image data needed to construct a URL at
	 * render time via Urls.imageUrl().  Returns null on failure.
	 */
	uploadImage: (file: File) => Promise<UploadedImageData | null>
}

export interface UploadStateArgs {
	/** crudl/ page config that owns the poster — identifies permissions + asset folders. */
	page:    string;
	folder:  string;
	/** When true, uploads are stored under the __SHARED directory instead of the site key. */
	shared: boolean ;
}

export function createUploadState(P: UploadStateArgs): UploadContext {
	return {
		uploadImage: (file: File) => uploadPosterImage(file, P.page, P.folder, P.shared),
	}
}

async function uploadPosterImage(file: File, page: string | undefined, folder: string, shared: boolean): Promise<UploadedImageData | null> {
	// Build the upload URL understood by handlePosterImageUpload.ts:
	//   /admin/posterImageUpload/<page>/<folder>
	// The poster builder uses a dedicated endpoint that converts raster images to a
	// single 'medium' version and accepts svgs as-is. The server uploads to the asset
	// store and returns the image data in the response body; the client stores it in
	// image on the image object via setImageUploadCmd.
	// When shared, ?siteKey=__SHARED tells the server to store the file under the
	// __SHARED directory instead of the current site key.
	const encPage   = encodeURIComponent(page ?? '')
	const uploadUrl = `/admin/posterImageUpload/${encPage}/${folder}${shared ? '?siteKey=__SHARED' : ''}`

	const formData = new FormData()
	formData.append('file', file)

	try {
		const res  = await fetch(uploadUrl, { method: 'POST', body: formData })
		const body = await res.json() as {
			hash?:      string
			formats?:   Record<string, { format: string } | undefined>
			dev?:       boolean
			errorType?: string
		}

		if (body.errorType) {
			console.error('[PosterUpload] Server error:', body.errorType)
			return null
		}

		if (!body.hash || !body.formats) return null

		return { hash: body.hash, formats: body.formats, dev: body.dev ?? false, folder, shared }
	} catch (err) {
		console.error('[PosterUpload] Upload failed:', err)
		return null
	}
}
